import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import {
    makeStyles,
    tokens,
    Spinner,
    Text,
    Tooltip,
    Button,
    CounterBadge,
    Toaster,
    useToastController,
    useId,
    Toast,
    ToastTitle,
    ToastBody,
} from "@fluentui/react-components";
import {
    ArrowUp16Regular,
    ArrowDown16Regular,
    ArrowSort16Regular,
    ArrowClockwise16Regular,
    Link16Regular,
    PersonAdd16Regular,
} from "@fluentui/react-icons";
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import AdminTopbar from "../AdminTopbar";
import GlobalTableSearch from "../ManageCustomers/GlobalTableSearch";
import TablePagination from "../TablePagination";
import { buildQueueColumns } from "./queueColumns";
import { useManageQueue } from "./useManageQueue";
import LinkSalesforceOrgDrawer from "./LinkSalesforceOrgDrawer";
import AssignMissedCallsUserDrawer from "./AssignMissedCallsUserDrawer";
import ConfirmationModal from "../ConfirmationModal";
import AdminService from "../AdminService";
import { formatDateTime } from "../adminUtil";

function formatSyncTimestamp(isoString) {
    const formatted = formatDateTime(isoString);
    return formatted === "-" ? "Last synced: Never" : `Last synced (UTC): ${formatted}`;
}

const useStyles = makeStyles({
    tableWrapper: {
        height: "calc(100vh - 58px)",
        display: "flex",
        flexDirection: "column",
    },
    topbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        position: "sticky",
        top: "0px",
        zIndex: 1000,
    },
    tableScroll: {
        flex: 1,
        overflowY: "auto",
    },
    tableHeader: {
        position: "sticky",
        top: 0,
        zIndex: 2,
        background: tokens.colorNeutralBackground2,
    },
    container: {
        padding: "8px 6px",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        background: tokens.colorNeutralBackground1,

        "& th, & td": {
            padding: "6px 8px",
            borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
            textAlign: "left",
        },

        "& thead th": {
            background: tokens.colorNeutralBackground2,
            fontWeight: 600,
        },
    },
    actions: {
        display: "flex",
        gap: "6px",
    },
    center: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "200px",
        flexDirection: "column",
        gap: "10px",
    },
    selectedCount: {
        fontSize: "12px",
        color: tokens.colorNeutralForeground2,
        paddingRight: "16px",
    },
    paginationWrapper: {
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        background: tokens.colorNeutralBackground1,
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        padding: "0px 8px",
    },
    topbarActions: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        paddingRight: "16px",
        flexShrink: 0,
    },
    syncIconSpinning: {
        display: "inline-flex",
        alignItems: "center",
        animationName: {
            from: { transform: "rotate(0deg)" },
            to: { transform: "rotate(360deg)" },
        },
        animationDuration: "0.8s",
        animationTimingFunction: "linear",
        animationIterationCount: "infinite",
    },
    syncWrapper: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    syncTimestamp: {
        fontSize: "11px",
        color: tokens.colorNeutralForeground3,
        whiteSpace: "nowrap",
        paddingRight: "8px",
        borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
        lineHeight: 1.4,
    },
    badge: {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        fontSize: "12px",
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForegroundLink,
    },
    selectionBar: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 16px",
        backgroundColor: tokens.colorBrandBackground2,
        borderBottom: `1px solid ${tokens.colorBrandStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        marginBottom: "6px",
    },
    selectionBarText: {
        fontSize: "13px",
        fontWeight: 600,
        color: tokens.colorBrandForegroundLink,
        flex: 1,
    },
});

export default function QueueTable({ searchTenantId, onBack } = {}) {
    const styles = useStyles();
    const toasterId = useId("queue-toaster");
    const { dispatchToast } = useToastController(toasterId);

    const notifySuccess = useCallback((message) => {
        dispatchToast(
            <Toast>
                <ToastTitle>Success</ToastTitle>
                <ToastBody>{message}</ToastBody>
            </Toast>,
            { intent: "success", timeout: 4000 },
        );
    }, [dispatchToast]);

    const notifyError = useCallback((message) => {
        dispatchToast(
            <Toast>
                <ToastTitle>Error</ToastTitle>
                <ToastBody>{message}</ToastBody>
            </Toast>,
            { intent: "error", timeout: 6000 },
        );
    }, [dispatchToast]);

    const {
        data,
        loading,
        error,
        totalCount,
        tenantInfo,
        sorting,
        setSorting,
        rowSelection,
        setRowSelection,
        handleUnlinkRequest,
        handleUnlinkConfirm,
        handleUnlinkCancel,
        unlinkTarget,
        unlinking,
        handleLinkSalesforceOrg,
        globalFilter,
        setGlobalFilter,
        pagination,
        setPagination,
        isGlobalSelectAll,
        setIsGlobalSelectAll,
        // Drawer
        drawerOpen,
        drawerOrgs,
        drawerLoading,
        drawerQueues,
        handleDrawerDismiss,
        handleLinkConfirm,
        linking,
        drawerIsSelectAll,
        drawerFilterSearch,
        // Bulk unlink
        bulkUnlinkOpen,
        bulkUnlinking,
        bulkUnlinkIsSelectAll,
        bulkUnlinkSelectedCount,
        bulkUnlinkLinkedCount,
        handleBulkUnlinkRequest,
        handleBulkUnlinkConfirm,
        handleBulkUnlinkCancel,
        refetch,
        patchQueueData,
        // Sync queues
        syncing,
        handleSyncQueues,
    } = useManageQueue({ searchTenantId });

    // ── Missed Calls Assignment state ────────────────────────────────────
    // { [queueId]: { userId, name, email } }
    const [missedCallsAssignments, setMissedCallsAssignments] = useState({});
    const [missedCallsDrawerOpen, setMissedCallsDrawerOpen] = useState(false);
    const [missedCallsTarget, setMissedCallsTarget] = useState(null); // { queues, isGlobalSelectAll }
    const [assigning, setAssigning] = useState(false);

    // ── Matched Record Assignment toggle state ───────────────────────────
    // { [queueId]: boolean } — seeded from server on each fetch; mutated optimistically on toggle.
    const [matchedObjAssignments, setMatchedObjAssignments] = useState({});
    // { [queueId]: true } — in-flight queues (spinner + disabled toggle). Absent = idle.
    const [matchedObjPending, setMatchedObjPending] = useState({});
    // Seed from server data whenever the queue list refreshes.
    // This is the source of truth — optimistic updates are already reflected by
    // refetch() (server has been updated), so overwriting is safe.
    useEffect(() => {
        if (!Array.isArray(data)) return;
        setMatchedObjAssignments(() => {
            const seeded = {};
            for (const q of data) {
                if (q?.queueId != null && typeof q.isObjectLinkingEnabled === "boolean") {
                    seeded[q.queueId] = q.isObjectLinkingEnabled;
                }
            }
            return seeded;
        });
    }, [data]);

    const handleToggleMatchedObjAssignment = useCallback(async (queueId, value) => {
        if (!queueId) return;
        // Read the previous value from server data (source of truth) rather than from
        // matchedObjAssignments — keeps this callback out of the deps that rebuild columns.
        const prevValue =
            (Array.isArray(data)
                ? data.find((q) => q.queueId === queueId)?.isObjectLinkingEnabled
                : undefined) ?? false;
        // Optimistic update + mark in-flight
        setMatchedObjAssignments((prev) => ({ ...prev, [queueId]: value }));
        setMatchedObjPending((prev) => ({ ...prev, [queueId]: true }));
        try {
            await AdminService.updateMatchedRecordAssignment({
                queueId,
                enabled: value,
                searchTenantId: searchTenantId || undefined,
            });
            // Keep data in sync so the seeding effect doesn't revert this change
            // if an unlink optimistic update fires before the next full refetch.
            patchQueueData(queueId, { isObjectLinkingEnabled: value });
            notifySuccess(
                `Matched Record Assignment ${value ? "enabled" : "disabled"} for this queue.`,
            );
        } catch (err) {
            // Rollback to the actual server-known value
            setMatchedObjAssignments((prev) => ({ ...prev, [queueId]: prevValue }));
            notifyError(err?.message || "Failed to update Matched Record Assignment.");
        } finally {
            setMatchedObjPending((prev) => {
                if (!prev[queueId]) return prev;
                const next = { ...prev };
                delete next[queueId];
                return next;
            });
        }
    }, [data, searchTenantId, notifySuccess, notifyError, patchQueueData]);

    // Stable ref so toast callbacks can read the latest selectedRows without deps
    const selectedRowsRef = useRef([]);

    const handleAssignMissedCalls = useCallback((queues, opts = {}) => {
        // If a single queue already has an assigned owner, seed the drawer's
        // radio selection with that user so it's pre-selected on open.
        let initialSelectedUser = null;
        if (queues.length === 1) {
            const q = queues[0];
            if (q.missedCallOwnerSfUserId && q.missedCallOwnerUsername) {
                initialSelectedUser = {
                    sfUserId: q.missedCallOwnerSfUserId,
                    username: q.missedCallOwnerUsername,
                    name: q.missedCallOwnerUsername,
                };
            }
        }
        setMissedCallsTarget({
            queues,
            isGlobalSelectAll: opts.isGlobalSelectAll ?? false,
            initialSelectedUser,
        });
        setMissedCallsDrawerOpen(true);
    }, []);

    const handleMissedCallsDrawerDismiss = useCallback(() => {
        setMissedCallsDrawerOpen(false);
        setMissedCallsTarget(null);
    }, []);

    const handleMissedCallsConfirm = useCallback(
        async (user, queues) => {
            const isSelectAll = missedCallsTarget?.isGlobalSelectAll;
            // Neither individual queues nor select-all — nothing to do.
            if (!isSelectAll && !queues?.length) return;

            setAssigning(true);
            try {
                const payload = {
                    sfUserId: user.sfUserId,
                    searchTenantId: searchTenantId || undefined,
                };
                if (isSelectAll) {
                    payload.selectionMode = "ALL";
                    if (globalFilter) payload.filters = { search: globalFilter };
                } else {
                    payload.queues = queues.map((q) => ({
                        queueId: q.queueId,
                        queueName: q.queueName,
                    }));
                }

                const res = await AdminService.assignQueueOwner(payload);
                const assignedCount = res?.data?.assignedCount ?? 0;
                const skippedCount = res?.data?.skippedCount ?? 0;

                // Optimistic local update for the affected queues (non-select-all only —
                // the refetch below is the source of truth in every case).
                if (!isSelectAll && assignedCount > 0) {
                    setMissedCallsAssignments((prev) => {
                        const next = { ...prev };
                        queues.forEach((q) => {
                            next[q.queueId ?? q.id] = {
                                sfUserId: user.sfUserId,
                                name: user.name,
                                username: user.username,
                                email: user.email,
                            };
                        });
                        return next;
                    });
                }

                refetch();
                setMissedCallsDrawerOpen(false);
                setMissedCallsTarget(null);

                const label = isSelectAll
                    ? `${assignedCount} queue${assignedCount === 1 ? "" : "s"}`
                    : queues.length === 1
                        ? `"${queues[0]?.queueName ?? "queue"}"`
                        : `${assignedCount} of ${queues.length} queues`;
                notifySuccess(
                    `Missed call assigned to ${user.name} for ${label}` +
                    (skippedCount > 0 ? ` (${skippedCount} skipped).` : "."),
                );
            } catch (err) {
                notifyError(err?.message || "Failed to assign missed call owner.");
            } finally {
                setAssigning(false);
            }
        },
        [missedCallsTarget, searchTenantId, globalFilter, notifySuccess, notifyError, refetch],
    );


    const handleLinkConfirmWithToast = useCallback(
        (tenantSfOrgId, queues, missedCallsUser) =>
            handleLinkConfirm(tenantSfOrgId, queues, {
                onSuccess: async () => {
                    notifySuccess("Salesforce org linked successfully.");

                    // Chain: assign the selected missed-calls user to the same set of queues.
                    // refetch() is always called at the end so the table reflects DB truth
                    // after both operations — regardless of whether assign-owner was run.
                    if (missedCallsUser?.sfUserId) {
                        setAssigning(true);
                        try {
                            const payload = {
                                sfUserId: missedCallsUser.sfUserId,
                                searchTenantId: searchTenantId || undefined,
                            };
                            if (drawerIsSelectAll) {
                                payload.selectionMode = "ALL";
                                if (drawerFilterSearch) {
                                    payload.filters = { search: drawerFilterSearch };
                                }
                            } else if (queues?.length) {
                                payload.queues = queues.map((q) => ({
                                    queueId: q.queueId,
                                    queueName: q.queueName,
                                }));
                            }

                            const res = await AdminService.assignQueueOwner(payload);
                            const assignedCount = res?.data?.assignedCount ?? 0;

                            // Optimistic local update for non-select-all so the column
                            // reflects the new owner immediately (refetch confirms it).
                            if (!drawerIsSelectAll && queues?.length) {
                                setMissedCallsAssignments((prev) => {
                                    const next = { ...prev };
                                    queues.forEach((q) => {
                                        next[q.queueId ?? q.id] = {
                                            sfUserId: missedCallsUser.sfUserId,
                                            sfOrgId: missedCallsUser.sfOrgId,
                                            name: missedCallsUser.name,
                                            username: missedCallsUser.username,
                                            email: missedCallsUser.email,
                                        };
                                    });
                                    return next;
                                });
                            }

                            notifySuccess(
                                `Missed call assigned to ${missedCallsUser.name} for ${assignedCount} queue${assignedCount === 1 ? "" : "s"}.`,
                            );
                        } catch (err) {
                            notifyError(
                                err?.message ||
                                "Salesforce org linked, but assigning the missed call owner failed.",
                            );
                        } finally {
                            setAssigning(false);
                        }
                    }
                    // refetch() is intentionally omitted here — handleLinkConfirm
                    // always calls fetchQueues() after onSuccess resolves.
                },
                onError: (err) =>
                    notifyError(err?.message || "Failed to link Salesforce org. Please try again."),
            }),
        [handleLinkConfirm, notifySuccess, notifyError, refetch, searchTenantId, drawerIsSelectAll, drawerFilterSearch],
    );

    // Wrap handleUnlinkConfirm to pass toast callbacks + clear missed calls assignment
    const handleUnlinkConfirmWithToast = useCallback(
        () =>
            handleUnlinkConfirm({
                onSuccess: () => {
                    notifySuccess("Salesforce org unlinked successfully.");
                    // Clear missed calls assignment for the unlinked queue
                    if (unlinkTarget?.queueId) {
                        setMissedCallsAssignments((prev) => {
                            const next = { ...prev };
                            delete next[unlinkTarget.queueId];
                            return next;
                        });
                    }
                },
                onError: (err) =>
                    notifyError(err?.message || "Failed to unlink Salesforce org. Please try again."),
            }),
        [handleUnlinkConfirm, notifySuccess, notifyError, unlinkTarget],
    );

    // Wrap handleBulkUnlinkConfirm to pass toast callbacks + clear missed calls assignments
    const handleBulkUnlinkConfirmWithToast = useCallback(
        () =>
            handleBulkUnlinkConfirm({
                onSuccess: () => {
                    notifySuccess("Queues unlinked from Salesforce org successfully.");
                    // Clear missed calls assignments for all unlinked queues
                    if (isGlobalSelectAll) {
                        setMissedCallsAssignments({});
                    } else {
                        const linkedIds = selectedRowsRef.current
                            .filter((r) => r.linkedSalesforceOrgId)
                            .map((r) => r.queueId ?? r.id);
                        if (linkedIds.length > 0) {
                            setMissedCallsAssignments((prev) => {
                                const next = { ...prev };
                                linkedIds.forEach((id) => delete next[id]);
                                return next;
                            });
                        }
                    }
                },
                onError: (err) =>
                    notifyError(err?.message || "Failed to unlink queues. Please try again."),
            }),
        [handleBulkUnlinkConfirm, notifySuccess, notifyError, isGlobalSelectAll],
    );

    const columns = useMemo(
        () => buildQueueColumns({
            onUnlink: handleUnlinkRequest,
            onLink: (row) => handleLinkSalesforceOrg([row], { isSelectAll: false, filterSearch: "" }),
            onAssignMissedCalls: handleAssignMissedCalls,
            missedCallsAssignments,
            matchedObjAssignments,
            matchedObjPending,
            onToggleMatchedObjAssignment: handleToggleMatchedObjAssignment,
            // Per-row: the column reads row.original.orgHasCliObjects (surfaced by
            // getQueuesData) to decide whether the toggle is enabled.
        }),
        [handleUnlinkRequest, handleLinkSalesforceOrg, handleAssignMissedCalls, missedCallsAssignments, matchedObjAssignments, matchedObjPending, handleToggleMatchedObjAssignment],
    );

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            rowSelection,
            pagination,
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        pageCount: Math.ceil(totalCount / pagination.pageSize) || 1,
        enableMultiSort: false,
        getRowId: (row) => row.queueId ?? row.id,
    });

    const selectedCount = Object.keys(rowSelection).length;
    const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);
    // Keep ref in sync so toast callbacks (bulk unlink) can read latest without stale closure
    selectedRowsRef.current = selectedRows;
    const rows = table.getRowModel().rows;
    // True when every row on the current page is checked
    const allPageSelected = rows.length > 0 && selectedCount === rows.length;
    // Disable bulk-unlink when none of the explicitly selected rows have a linked org.
    // When select-all is active we can't know counts for other pages, so keep enabled.
    const noLinkedSelected = !isGlobalSelectAll && selectedRows.every((r) => !r.linkedSalesforceOrgId);
    // For Assign Missed Call: always requires at least one visible linked row to derive
    // the Salesforce org, regardless of isGlobalSelectAll.
    const noLinkedVisibleSelected = selectedRows.every((r) => !r.linkedSalesforceOrgId);

    return (
        <>
            {/* ── Toast notifications ───────────────────────────────────── */}
            <Toaster toasterId={toasterId} position="top-end" />

            {/* ── Link Salesforce Org Drawer ────────────────────────────── */}
            <LinkSalesforceOrgDrawer
                open={drawerOpen}
                onDismiss={handleDrawerDismiss}
                queues={drawerQueues}
                orgs={drawerOrgs}
                loading={drawerLoading}
                linking={linking}
                assigning={assigning}
                onConfirm={handleLinkConfirmWithToast}
                isGlobalSelectAll={isGlobalSelectAll}
                totalCount={totalCount}
                tenantName={searchTenantId ? tenantInfo?.tenantName : undefined}
                searchTenantId={searchTenantId}
            />

            {/* ── Assign Missed Call Owner Drawer ─────────────────── */}
            <AssignMissedCallsUserDrawer
                open={missedCallsDrawerOpen}
                onDismiss={handleMissedCallsDrawerDismiss}
                queues={missedCallsTarget?.queues ?? []}
                onConfirm={handleMissedCallsConfirm}
                isGlobalSelectAll={missedCallsTarget?.isGlobalSelectAll ?? false}
                totalCount={totalCount}
                assigning={assigning}
                initialSelectedUser={missedCallsTarget?.initialSelectedUser ?? null}
                searchTenantId={searchTenantId}
            />

            {/* ── Unlink confirmation modal (single row) ───────────────── */}
            <ConfirmationModal
                isOpen={!!unlinkTarget}
                type="warning"
                title="Unlink Salesforce org"
                message={
                    unlinkTarget
                        ? `Unlink the Salesforce org from "${unlinkTarget.queueName}"? The queue will no longer be associated with any Salesforce org.`
                        : ""
                }
                confirmText="Unlink"
                onConfirm={handleUnlinkConfirmWithToast}
                onCancel={handleUnlinkCancel}
                loading={unlinking}
            />

            {/* ── Bulk unlink confirmation modal ────────────────────────── */}
            <ConfirmationModal
                isOpen={bulkUnlinkOpen}
                type="warning"
                title="Unlink Salesforce org"
                message={
                    bulkUnlinkIsSelectAll
                        ? "All queues currently linked to a Salesforce org will be unlinked. Queues with no linked org are not affected. They will no longer be associated with any Salesforce org."
                        : bulkUnlinkLinkedCount === 0
                            ? `None of the ${bulkUnlinkSelectedCount} selected ${bulkUnlinkSelectedCount === 1 ? "queue" : "queues"} are linked to a Salesforce org.`
                            : bulkUnlinkLinkedCount === bulkUnlinkSelectedCount
                                ? `${bulkUnlinkLinkedCount} ${bulkUnlinkLinkedCount === 1 ? "queue" : "queues"} will be unlinked from their Salesforce org. ${bulkUnlinkLinkedCount === 1 ? "It" : "They"} will no longer be associated with any Salesforce org.`
                                : `${bulkUnlinkLinkedCount} of ${bulkUnlinkSelectedCount} selected ${bulkUnlinkSelectedCount === 1 ? "queue" : "queues"} are linked and will be unlinked. The rest will be skipped. They will no longer be associated with any Salesforce org.`
                }
                confirmText="Unlink"
                onConfirm={handleBulkUnlinkConfirmWithToast}
                onCancel={handleBulkUnlinkCancel}
                loading={bulkUnlinking}
            />

            {/* ── Topbar ────────────────────────────────────────────────────── */}
            <header className={styles.topbar}>
                <AdminTopbar
                    title={
                        searchTenantId && tenantInfo?.tenantName
                            ? `Queues \u2013 ${tenantInfo.tenantName}`
                            : "Manage Queue"
                    }
                    onBack={onBack}
                    titleExtra={
                        <Tooltip content="Total queues" relationship="label">
                            {loading ? (
                                <Spinner size="tiny" style={{ marginLeft: 4 }} />
                            ) : (
                                <CounterBadge
                                    className={styles.badge}
                                    count={totalCount}
                                    overflowCount={9999}
                                    showZero
                                />
                            )}
                        </Tooltip>
                    }
                />
                <div className={styles.topbarActions}>
                    <div className={styles.syncWrapper}>
                        <Text className={styles.syncTimestamp}>
                            {syncing ? "Syncing..." : formatSyncTimestamp(tenantInfo?.lastSyncQueuesAt)}
                        </Text>
                        <Tooltip
                            content={
                                syncing
                                    ? "Syncing queues, please wait..."
                                    : "Fetch all queues and sync to the database"
                            }
                            relationship="label"
                        >
                            <Button
                                size="small"
                                appearance="outline"
                                icon={
                                    <span className={syncing ? styles.syncIconSpinning : undefined}>
                                        <ArrowClockwise16Regular />
                                    </span>
                                }
                                disabled={syncing || loading}
                                style={
                                    !syncing && !loading
                                        ? { color: tokens.colorBrandForeground1, borderColor: tokens.colorBrandStroke2 }
                                        : undefined
                                }
                                onClick={() => {
                                    handleSyncQueues({
                                        onSuccess: (res) => {
                                            const s = res?.syncSummary;
                                            if (s) {
                                                const parts = [];
                                                if (s.insertedCount) parts.push(`${s.insertedCount} added`);
                                                if (s.updatedCount) parts.push(`${s.updatedCount} updated`);
                                                if (s.skippedCount) parts.push(`${s.skippedCount} unchanged`);
                                                const detail = parts.length ? ` (${parts.join(", ")})` : "";
                                                notifySuccess(
                                                    `Synced ${s.fetchedCount ?? 0} queue${s.fetchedCount === 1 ? "" : "s"}${detail}.`,
                                                );
                                            } else {
                                                notifySuccess("Queues synced successfully.");
                                            }
                                        },
                                        onError: (err) => {
                                            const fallback =
                                                err?.status === 400 ? "Invalid request. Please check the tenant configuration." :
                                                err?.status === 403 ? "You are not authorized to sync queues." :
                                                err?.status === 404 ? "Tenant not found or is inactive." :
                                                "Failed to sync queues. Please try again.";
                                            notifyError(err?.message || fallback);
                                        },
                                    });
                                }}
                            >
                                {syncing ? "Syncing..." : "Sync Queues"}
                            </Button>
                        </Tooltip>
                    </div>
                    <GlobalTableSearch
                        value={globalFilter}
                        onChange={setGlobalFilter}
                        placeholder="Search queues..."
                    />
                </div>
            </header>

            {/* ── Container + TableWrapper ───────────────────────────────────── */}
            <div className={styles.container}>
                <div className={styles.tableWrapper}>

                    {/* ── Selection action bar ───────────────────────────────── */}
                    {selectedCount > 0 && (
                        <div className={styles.selectionBar}>
                            <Text className={styles.selectionBarText}>
                                {isGlobalSelectAll
                                    ? `All ${totalCount} queues selected`
                                    : `${selectedCount} ${selectedCount === 1 ? "queue" : "queues"} selected`}
                            </Text>

                            {/* Offer to select ALL queues across all pages */}
                            {allPageSelected && !isGlobalSelectAll && totalCount > selectedCount && (
                                <Button
                                    size="small"
                                    appearance="subtle"
                                    onClick={() => setIsGlobalSelectAll(true)}
                                >
                                    Select all {totalCount} queues
                                </Button>
                            )}

                            <Tooltip content="Link Salesforce Org" relationship="label">
                                <Button
                                    size="small"
                                    appearance="primary"
                                    icon={<Link16Regular />}
                                    onClick={() => {
                                        handleLinkSalesforceOrg(selectedRows, {
                                            isSelectAll: isGlobalSelectAll,
                                            filterSearch: globalFilter,
                                        });
                                    }}
                                >
                                    Link Salesforce Org
                                </Button>
                            </Tooltip>
                            <Button
                                size="small"
                                appearance="subtle"
                                disabled={noLinkedSelected}
                                onClick={() => {
                                    handleBulkUnlinkRequest(selectedRows, {
                                        isSelectAll: isGlobalSelectAll,
                                        filterSearch: globalFilter,
                                    });
                                }}
                            >
                                Unlink Salesforce Org
                            </Button>
                            <Tooltip
                                content={
                                    noLinkedVisibleSelected
                                        ? "Select at least one queue linked to a Salesforce org"
                                        : "Assign missed call owner for selected queues"
                                }
                                relationship="label"
                            >
                                <span>
                                    <Button
                                        size="small"
                                        appearance="subtle"
                                        icon={<PersonAdd16Regular />}
                                        disabled={noLinkedVisibleSelected}
                                        onClick={() => {
                                            const linkedRows = selectedRows.filter((r) => r.linkedSalesforceOrgId);
                                            if (!linkedRows.length) return;
                                            handleAssignMissedCalls(linkedRows, { isGlobalSelectAll });
                                        }}
                                        style={
                                            !noLinkedVisibleSelected
                                                ? { color: "#7160e8" }
                                                : undefined
                                        }
                                    >
                                        Assign Missed Call
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button
                                size="small"
                                appearance="subtle"
                                onClick={() => {
                                    setRowSelection({});
                                    setIsGlobalSelectAll(false);
                                }}
                            >
                                Clear selection
                            </Button>
                        </div>
                    )}
                    <div className={styles.tableScroll}>
                        {loading ? (
                            <div className={styles.center}>
                                <Spinner size="large" />
                                <Text>Loading queues...</Text>
                            </div>
                        ) : error ? (
                            <div className={styles.center}>
                                <Text>{error?.message || "Failed to load queues. Please try again."}</Text>
                            </div>
                        ) : (
                            <table className={styles.table}>
                                <thead className={styles.tableHeader}>
                                    {table.getHeaderGroups().map((hg) => (
                                        <tr key={hg.id}>
                                            {hg.headers.map((header) => (
                                                <th
                                                    key={header.id}
                                                    style={{
                                                        width: header.column.columnDef.size
                                                            ? header.column.columnDef.size
                                                            : undefined,
                                                        cursor: header.column.getCanSort()
                                                            ? "pointer"
                                                            : "default",
                                                    }}
                                                    onClick={(e) =>
                                                        header.column.getToggleSortingHandler()?.(e)
                                                    }
                                                >
                                                    <span
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext(),
                                                        )}
                                                        {header.column.getCanSort() && (
                                                            <Tooltip
                                                                content={
                                                                    header.column.getIsSorted()
                                                                        ? `Sorted ${header.column.getIsSorted() ===
                                                                            "asc"
                                                                            ? "ascending"
                                                                            : "descending"
                                                                        }. Hold Shift to multi-sort.`
                                                                        : "Click to sort. Hold Shift to multi-sort."
                                                                }
                                                                relationship="label"
                                                            >
                                                                <span
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        marginLeft: 4,
                                                                    }}
                                                                >
                                                                    {header.column.getIsSorted() ===
                                                                        "asc" && <ArrowUp16Regular />}
                                                                    {header.column.getIsSorted() ===
                                                                        "desc" && (
                                                                            <ArrowDown16Regular />
                                                                        )}
                                                                    {!header.column.getIsSorted() && (
                                                                        <ArrowSort16Regular />
                                                                    )}
                                                                    {header.column.getIsSorted() && (
                                                                        <span
                                                                            style={{
                                                                                fontSize: 10,
                                                                                marginLeft: 2,
                                                                            }}
                                                                        >
                                                                            {header.column.getSortIndex() +
                                                                                1}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>

                                <tbody>
                                    {rows.length > 0 ? (
                                        rows.map((row) => (
                                            <tr
                                                key={row.id}
                                                style={{
                                                    background: row.getIsSelected()
                                                        ? tokens.colorSubtleBackgroundSelected
                                                        : undefined,
                                                }}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <td key={cell.id}>
                                                        {flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext(),
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={columns.length}
                                                style={{ padding: "32px 16px", textAlign: "center" }}
                                            >
                                                <Text>No queues found.</Text>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* ── Pagination ─────────────────────────────────────────── */}
                    <div className={styles.paginationWrapper}>
                        <TablePagination
                            table={table}
                            totalCount={totalCount}
                            pagination={pagination}
                            setPagination={setPagination}
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
